import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  RecoveryFlow,
  SettingsFlow,
  UpdateRecoveryFlowBody,
  UpdateSettingsFlowWithPasswordMethod,
} from '@ory/client';

import { VKratosFormFieldDirective } from 'src/app/core/components/kratos-form/form-slot.directive';
import { KratosFormV2Component } from 'src/app/core/components/kratos-form/form.component';
import { SharedModule } from 'src/app/core/shared.module';
import {
  ErrAlreadyLoggedIn,
  ErrBrowserLocationChangeRequired,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { VCardComponent } from '../../../core/components/ui-kit/card/card.component';
import { VBottomGradientWrapperComponent } from '../components/bottom-gradient/bottom-gradient.component';
import { VLogoHeaderComponent } from '../components/logo-header/logo-header.component';

@Component({
  selector: 'app-recovery-page',
  templateUrl: './recovery.component.html',
  imports: [
    SharedModule,
    VCardComponent,
    RouterModule,
    KratosFormV2Component,
    VKratosFormFieldDirective,
    VBottomGradientWrapperComponent,
    VLogoHeaderComponent,
  ],
})
export class RecoveryPageComponent implements OnInit {
  private auth = inject(KratosAuthenticationService);
  private router = inject(Router);
  private toast = inject(ToastService);

  recoveryFlow = signal<RecoveryFlow | undefined>(undefined);
  settingsFlow = signal<SettingsFlow | undefined>(undefined);
  isCompletingRegistrationFlow = false;
  isCompletingSettingsFlow = false;

  stage: 'confirm-code' | 'reset-password' = 'confirm-code';

  initialFlowLoading = true;

  ngOnInit(): void {
    this.loadRecoveryFlow();
  }

  // Starts the recovery process by loading a flow which sends a code to the
  // email address a user entered.

  private loadRecoveryFlow() {
    this.auth
      .getRecoveryFlow()
      .then(flow => {
        this.recoveryFlow.set(flow);
      })
      .catch(ex => {
        if (ex instanceof ErrAlreadyLoggedIn) {
          this.stage = 'reset-password';
          this.loadSettingsFlow();
        }
      })
      .finally(() => {
        this.initialFlowLoading = false;
      });
  }

  async submitFlow(data: UpdateRecoveryFlowBody) {
    this.isCompletingRegistrationFlow = true;
    await this.auth
      .completeRecoveryFlow(this.recoveryFlow()!, data)
      .then(newFlow => {
        this.recoveryFlow.set(newFlow);
      })
      .catch(err => {
        if (err instanceof ErrBrowserLocationChangeRequired) {
          this.stage = 'reset-password';
          this.loadSettingsFlow();
        } else {
          this.recoveryFlow = err.response.data;
        }
      })
      .finally(() => {
        this.isCompletingRegistrationFlow = false;
      });
  }

  // Settings flow is used to reset the password after successful validation
  // of email.

  private loadSettingsFlow() {
    this.auth
      .getSettingsFlow()
      .then(flow => {
        this.settingsFlow.set(flow);
      })
      .catch(ex => {
        if (ex instanceof ErrTwoFARequired) {
          this.router.navigate(['/login'], {
            queryParams: { aal: 'aal2', returnTo: '/recovery' },
          });
        }
      });
  }

  async submitSettingsFlow(data: UpdateSettingsFlowWithPasswordMethod) {
    this.isCompletingSettingsFlow = true;
    await this.auth
      .completeSettingsFlow(this.settingsFlow()!.id, 'password', {
        ...data,
        method: 'password',
      })
      .then(() => {
        this.toast.showInfo(
          'pages.recover.passwordUpdatedToast.header',
          'pages.recover.passwordUpdatedToast.body'
        );
        this.router.navigate(['/login']);
      })
      .catch(err => {
        this.settingsFlow.set(err.response.data);
      })
      .finally(() => {
        this.isCompletingSettingsFlow = false;
      });
  }
}
